import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserCheck, Plus, Edit, Trash2, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { useToast } from '../../../hooks/useToast';
import { AvailabilityScheduler, type TiemposDisponibles } from '../../../components/AvailabilityScheduler';
import {
  getTeachers,
  createTeacher,
  updateTeacher,
  deleteTeacher,
  updateTeacherCourses,
  type Teacher,
  type TeacherInput,
} from '../../../hooks/teachersService';

interface User {
  id: number;
  fullName: string;
  email: string;
}

interface Course {
  id: number;
  code: string;
  name: string;
}

export default function Profesores() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [selectedCourseIds, setSelectedCourseIds] = useState<number[]>([]);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<{
    userId: string;
    nombre: string;
    telefono: string;
    contactoEmergencia: string;
    domicilio: string;
    tiemposDisponibles: TiemposDisponibles | null;
    aperturacierre: boolean;
    maximoClases: number;
    tipoContrato: string;
  }>({
    userId: '',
    nombre: '',
    telefono: '',
    contactoEmergencia: '',
    domicilio: '',
    tiemposDisponibles: null,
    aperturacierre: false,
    maximoClases: 0,
    tipoContrato: 'N/A',
  });

  useEffect(() => {
    fetchTeachers();
    fetchUsers();
    fetchCourses();
  }, []);

  const fetchTeachers = async () => {
    try {
      const data = await getTeachers();
      const sorted = Array.isArray(data) ? data.sort((a: any, b: any) => a.nombre.localeCompare(b.nombre, 'es')) : data;
      setTeachers(sorted);
    } catch (error) {
      console.error('Error fetching teachers:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los profesores',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await fetch('/api/curriculum-courses', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableCourses(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const handleUserChange = (userId: string) => {
    setFormData((prev) => ({ ...prev, userId }));
    if (userId) {
      const selectedUser = users.find((u) => u.id === parseInt(userId));
      if (selectedUser) {
        setFormData((prev) => ({ ...prev, nombre: selectedUser.fullName }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const teacherData: TeacherInput = {
        userId: formData.userId ? parseInt(formData.userId) : null,
        nombre: formData.nombre,
        telefono: formData.telefono || null,
        contactoEmergencia: formData.contactoEmergencia || null,
        domicilio: formData.domicilio || null,
        tiemposDisponibles: formData.tiemposDisponibles || null,
        aperturacierre: formData.aperturacierre,
        maximoClases: formData.maximoClases,
        tipoContrato: formData.tipoContrato,
      };

      let savedTeacher: Teacher;
      if (editingTeacher) {
        savedTeacher = await updateTeacher(editingTeacher.id, teacherData);
      } else {
        savedTeacher = await createTeacher(teacherData);
      }

      await updateTeacherCourses(savedTeacher.id, { courseIds: selectedCourseIds });

      toast({
        title: 'Éxito',
        description: editingTeacher
          ? 'Profesor actualizado correctamente'
          : 'Profesor creado correctamente',
      });
      setIsDialogOpen(false);
      resetForm();
      fetchTeachers();
    } catch (error: any) {
      console.error('Error saving teacher:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.error || 'Error al guardar profesor',
      });
    }
  };

  const handleEdit = async (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      userId: teacher.userId?.toString() || '',
      nombre: teacher.nombre,
      telefono: teacher.telefono || '',
      contactoEmergencia: teacher.contactoEmergencia || '',
      domicilio: teacher.domicilio || '',
      tiemposDisponibles: teacher.tiemposDisponibles || null,
      aperturacierre: teacher.aperturacierre || false,
      maximoClases: teacher.maximoClases || 0,
      tipoContrato: teacher.tipoContrato || 'N/A',
    });
    setSelectedCourseIds(teacher.courses?.map((c) => c.id) || []);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('¿Estás seguro de eliminar este profesor?')) return;

    try {
      await deleteTeacher(id);
      toast({
        title: 'Éxito',
        description: 'Profesor eliminado correctamente',
      });
      fetchTeachers();
    } catch (error: any) {
      console.error('Error deleting teacher:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.error || 'Error al eliminar profesor',
      });
    }
  };

  const resetForm = () => {
    setEditingTeacher(null);
    setFormData({
      userId: '',
      nombre: '',
      telefono: '',
      contactoEmergencia: '',
      domicilio: '',
      tiemposDisponibles: null,
      aperturacierre: false,
      maximoClases: 0,
      tipoContrato: 'N/A',
    });
    setSelectedCourseIds([]);
    setCourseSearchTerm('');
  };

  const toggleCourse = (courseId: number) => {
    setSelectedCourseIds((prev) =>
      prev.includes(courseId) ? prev.filter((id) => id !== courseId) : [...prev, courseId]
    );
  };

  const filteredCourses = availableCourses.filter(
    (course) =>
      course.name.toLowerCase().includes(courseSearchTerm.toLowerCase()) ||
      course.code.toLowerCase().includes(courseSearchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/app/catalogos')}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">Profesores</h1>
        </div>
        <p className="text-gray-600 ml-12">Gestión de profesores y materias que imparten</p>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Catálogo de Profesores</h2>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Profesor
          </Button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Buscar por nombre, contacto emergencia..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {loading ? (
          <p className="text-gray-500 text-center py-8">Cargando...</p>
        ) : teachers.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <UserCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No hay profesores registrados</p>
          </div>
        ) : (
          <div className="w-full">
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Usuario Sistema
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Contacto Emergencia
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Materias
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {teachers
                  .filter((teacher) =>
                    teacher.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (teacher.contactoEmergencia || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (teacher.userName || '').toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .map((teacher) => (
                  <tr key={teacher.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {teacher.nombre}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {teacher.userName ? (
                        <div>
                          <div>{teacher.userName}</div>
                          <div className="text-xs text-gray-400">{teacher.userEmail}</div>
                        </div>
                      ) : (
                        <span className="text-gray-400">Sin asignar</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {teacher.contactoEmergencia || 'No especificado'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {teacher.courses && teacher.courses.length > 0 ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {teacher.courses.length} materia(s)
                        </span>
                      ) : (
                        <span className="text-gray-400">Sin materias</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(teacher)}
                        className="text-blue-600 hover:text-blue-900 mr-4"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(teacher.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTeacher ? 'Editar Profesor' : 'Nuevo Profesor'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="userId">Usuario del Sistema (Opcional)</Label>
                <select
                  id="userId"
                  value={formData.userId}
                  onChange={(e) => handleUserChange(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">Sin vincular a usuario</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.fullName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-span-2">
                <Label htmlFor="nombre">Nombre del Profesor *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  placeholder="Nombre completo"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="telefono">Teléfono del Profesor</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="Número de teléfono"
                  type="tel"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="contactoEmergencia">Contacto de Emergencia</Label>
                <Input
                  id="contactoEmergencia"
                  value={formData.contactoEmergencia}
                  onChange={(e) =>
                    setFormData({ ...formData, contactoEmergencia: e.target.value })
                  }
                  placeholder="Teléfono o contacto de emergencia"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="domicilio">Domicilio (Opcional)</Label>
                <textarea
                  id="domicilio"
                  value={formData.domicilio}
                  onChange={(e) => setFormData({ ...formData, domicilio: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                  placeholder="Dirección completa"
                />
              </div>

              <div className="col-span-2">
                <Label>Disponibilidad para Clases</Label>
                <p className="text-sm text-gray-500 mb-3">
                  Define los días y horarios en que el profesor está disponible para dar clases
                </p>
                <AvailabilityScheduler
                  value={formData.tiemposDisponibles}
                  onChange={(value) => setFormData({ ...formData, tiemposDisponibles: value })}
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <span className="w-1.5 h-6 bg-blue-600 rounded-full"></span>
                Características Especiales
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="aperturacierre" className="flex items-center gap-2">
                    <span>Apertura/Cierre</span>
                  </Label>
                  <div className="flex items-center gap-3 mt-2">
                    <input
                      id="aperturacierre"
                      type="checkbox"
                      checked={formData.aperturacierre}
                      onChange={(e) => setFormData({ ...formData, aperturacierre: e.target.checked })}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-600">
                      {formData.aperturacierre ? 'Habilitado' : 'Deshabilitado'}
                    </span>
                  </div>
                </div>

                <div>
                  <Label htmlFor="maximoClases">Máximo de Clases</Label>
                  <input
                    id="maximoClases"
                    type="number"
                    min="0"
                    value={formData.maximoClases}
                    onChange={(e) => setFormData({ ...formData, maximoClases: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">Número máximo de clases que puede impartir</p>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="tipoContrato">Tipo de Contrato</Label>
                  <select
                    id="tipoContrato"
                    value={formData.tipoContrato}
                    onChange={(e) => setFormData({ ...formData, tipoContrato: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="N/A">N/A</option>
                    <option value="X HORAS">X HORAS</option>
                    <option value="FIJO">FIJO</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <Label>Materias que puede impartir</Label>
              <p className="text-sm text-gray-500 mb-3">
                Selecciona las materias que este profesor puede impartir
              </p>

              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Buscar materias por código o nombre..."
                  value={courseSearchTerm}
                  onChange={(e) => setCourseSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="border rounded-lg max-h-60 overflow-y-auto">
                {filteredCourses.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No hay materias disponibles</p>
                ) : (
                  <div className="divide-y">
                    {filteredCourses.map((course) => (
                      <label
                        key={course.id}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedCourseIds.includes(course.id)}
                          onChange={() => toggleCourse(course.id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">
                              {course.code}
                            </span>
                            <span className="text-sm text-gray-600">{course.name}</span>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {selectedCourseIds.length > 0 && (
                <p className="text-sm text-gray-600 mt-2">
                  {selectedCourseIds.length} materia(s) seleccionada(s)
                </p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingTeacher ? 'Actualizar' : 'Crear'} Profesor
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
